import moment from 'moment'
import {format} from 'date-fns/format'
import {CID, USER_INFO} from '@constants/auth.constant'
import {LocalStorageService} from '@services/utils/localStorage.service'

/**
 * The per-company date/date-time patterns delivered on the session (userInfo.companies[*].dateFormat
 * / dateTimeFormat) are Java SimpleDateFormat/DateTimeFormatter patterns (e.g. "yyyy-MM-dd HH:mm:ss").
 * moment uses different tokens, so convert same-letter runs Java -> moment before formatting.
 */
const JAVA_TO_MOMENT: Record<string, string> = {
  yyyy: 'YYYY',
  yy: 'YY',
  MMMM: 'MMMM',
  MMM: 'MMM',
  MM: 'MM',
  M: 'M',
  dd: 'DD',
  d: 'D',
  EEEE: 'dddd',
  EEE: 'ddd',
  E: 'ddd',
  HH: 'HH',
  H: 'H',
  hh: 'hh',
  h: 'h',
  mm: 'mm',
  m: 'm',
  ss: 'ss',
  s: 's',
  SSS: 'SSS',
  a: 'A',
}

export const javaToMoment = (fmt: string): string =>
  (fmt || '').replace(/([a-zA-Z])\1*/g, (run) => JAVA_TO_MOMENT[run] ?? run)

// Active company (by cid) carries dateFormat + dateTimeFormat in the session userInfo.companies.
const activeCompany = (): any => {
  const userInfo = LocalStorageService.get(USER_INFO)
  const cid = LocalStorageService.get(CID)
  const companies = userInfo?.companies || []
  return companies.find((c: any) => c.companyId === cid) || companies[0] || {}
}

export const getDateFormat = (): string => activeCompany()?.dateFormat || 'yyyy-MM-dd'
export const getDateTimeFormat = (): string => activeCompany()?.dateTimeFormat || 'yyyy-MM-dd HH:mm:ss'

/** Format a value with the company's date pattern (date only). */
export const formatDate = (value?: any, fmt?: string): string =>
  value ? moment(value).format(javaToMoment(fmt || getDateFormat())) : '—'

/** Format a value with the company's date-time pattern (date + time). */
export const formatDateTime = (value?: any, fmt?: string): string =>
  value ? moment(value).format(javaToMoment(fmt || getDateTimeFormat())) : '—'

/**
 * A document's date with the time it was raised, in the company's date-time pattern.
 *
 * The document date columns (requisition_date, po_date…) are DATE only, so the clock comes from
 * the row's inserted_on. The day stays the document's own: a backdated PO keeps the date it is
 * dated, not the day it was keyed in. With no inserted_on it is the date alone.
 */
export const formatDocDateTime = (date?: any, insertedOn?: any): string => {
  if (!date) return '—'
  const at = insertedOn ? moment(insertedOn) : null
  if (!at?.isValid()) return formatDate(date)
  return moment(date)
    .set({hour: at.hours(), minute: at.minutes(), second: at.seconds(), millisecond: 0})
    .format(javaToMoment(getDateTimeFormat()))
}

const pad = (n: number) => String(n).padStart(2, '0')

/**
 * A date as yyyy-MM-dd from its LOCAL fields. toISOString() gives the UTC date, which here (+6)
 * is the previous day for anything before 06:00 — including react-datepicker's local midnight.
 */
export const toYmd = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

/** Today on the local clock, as yyyy-MM-dd. */
export const todayYmd = (): string => toYmd(new Date())

/**
 * The company date pattern in date-fns tokens, for the date picker (@components/DateInput).
 *
 * date-fns reads Java's letters the same way except two it refuses to format without an opt-in:
 * Y (week year) and D (day of year). Someone typing a pattern means year and day by them, so they
 * are mapped. Time letters are dropped. The setting is free text, so a pattern that still can't
 * format, or lacks a day, month or year, falls back to yyyy-MM-dd rather than break every form.
 */
export const getPickerDateFormat = (): string => {
  const fmt = getDateFormat()
    .replace(/([a-zA-Z])\1*/g, (run) =>
      run[0] === 'Y'
        ? run.toLowerCase()
        : run[0] === 'D'
        ? 'd'.repeat(run.length)
        : /[HhKkmsSaAzZX]/.test(run[0])
        ? ''
        : run
    )
    .replace(/[\s:.,]+$/, '')
  try {
    format(new Date(2000, 0, 31), fmt)
    if (/y/.test(fmt) && /[ML]/.test(fmt) && /d/.test(fmt)) return fmt
  } catch {
    // an invalid pattern: fall through to the default
  }
  return 'yyyy-MM-dd'
}
